<template>
  <v-container fluid class="purchase-list-page pa-0">
    <v-dialog v-model="loadingDialog" hide-overlay persistent width="300">
      <v-card color="primary" dark>
        <v-container>
          <div class="mb-3">Please stand by deleting purchase</div>
          <v-progress-linear
            indeterminate
            color="white"
            class="mb-0"
          />
        </v-container>
      </v-card>
    </v-dialog>

    <MakePayment
      :VendorId="selectedPurchase ? selectedPurchase.VendorId : null"
      :createDialog="makePaymentDialog"
      @close="makePaymentDialog = false"
      @submit="getList()"
      :purchase="selectedPurchase"
      :purchaseMakePayment="true"
    />

    <v-card :loading="loading" outlined class="purchase-list-card">
      <div class="purchase-list-header">
        <div class="purchase-title-wrap">
          <v-btn v-if="!VendorId" icon small class="mr-2" @click="$router.go(-1)">
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>

          <div>
            <div class="page-title">Purchase</div>
            <div class="page-subtitle">
              {{ totalItems }} purchase{{ totalItems === 1 ? "" : "s" }} found
            </div>
          </div>
        </div>

        <div class="purchase-actions">
          <v-btn
            v-if="checkRightStatus(6)"
            small
            rounded
            color="primary"
            dark
            @click="add"
          >
            <v-icon left small>mdi-plus</v-icon>
            New
          </v-btn>

          <v-btn
            v-if="PurchaseDetails.length !== 0 && checkRightStatus(6)"
            small
            rounded
            outlined
            @click="openRunning"
          >
            <v-icon left small>mdi-history</v-icon>
            Running Purchase
          </v-btn>
        </div>
      </div>

      <div class="purchase-filter-bar">
        <v-text-field
          v-model="search"
          outlined
          prepend-inner-icon="search"
          autofocus
          dense
          label="Search purchase, vendor, reference"
          single-line
          hide-details
          clearable
          class="purchase-search"
        />

        <template v-if="$vuetify.breakpoint.mdAndUp">
          <v-btn-toggle
            v-model="purchaseStatus"
            @change="getList()"
            dense
            mandatory
            class="status-toggle"
          >
            <v-btn value="all" small>All</v-btn>
            <v-btn value="open" small>Open</v-btn>
            <v-btn value="paid" small>Paid</v-btn>
          </v-btn-toggle>

          <v-spacer />

          <DateTimePicker
            v-if="!showAll"
            defaultRange="since60DaysAgo"
            :range="true"
            :hideTime="true"
            v-model="dateTime"
            @submit="getList()"
          />
        </template>

        <v-btn
          v-else
          icon
          @click="mobileFilterSheet = true"
        >
          <v-icon>mdi-tune-variant</v-icon>
        </v-btn>
      </div>

      <v-bottom-sheet
        v-model="mobileFilterSheet"
        inset
        max-width="520"
      >
        <v-card class="mobile-filter-card">
          <div class="mobile-filter-header">
            <v-btn icon @click="mobileFilterSheet = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>

            <div class="mobile-filter-title">Filters</div>
          </div>

          <v-card-text>
            <v-btn-toggle
              v-model="purchaseStatus"
              @change="getList()"
              mandatory
              dense
              class="mobile-status-toggle"
            >
              <v-btn value="all">All</v-btn>
              <v-btn value="open">Open</v-btn>
              <v-btn value="paid">Paid</v-btn>
            </v-btn-toggle>

            <v-divider class="my-4" />

            <DateTimePicker
              v-if="!showAll"
              defaultRange="since60DaysAgo"
              :range="true"
              :hideTime="true"
              v-model="dateTime"
              @submit="getList()"
            />

            <v-btn
              block
              color="primary"
              rounded
              depressed
              class="mt-4"
              @click="mobileFilterSheet = false"
            >
              Apply
            </v-btn>
          </v-card-text>
        </v-card>
      </v-bottom-sheet>

      <v-card-text class="pa-4">
        <div class="summary-row mb-4">
          <div class="summary-box">
            <div class="summary-label">List Total</div>
            <div class="summary-value">{{ total | currency }}</div>
          </div>

          <div class="summary-box">
            <div class="summary-label">List Due</div>
            <div class="summary-value">{{ balance | currency }}</div>
          </div>

          <div class="summary-box">
            <div class="summary-label">Result Total</div>
            <div class="summary-value">{{ fullTotal | currency }}</div>
          </div>

          <div class="summary-box">
            <div class="summary-label">Result Due</div>
            <div class="summary-value">{{ fullBalance | currency }}</div>
          </div>
        </div>

        <v-data-table
          :mobile-breakpoint="0"
          :headers="headers"
          :items="Purchases"
          :loading="loading"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          :page.sync="page"
          :items-per-page.sync="itemsPerPage"
          :server-items-length="totalItems"
          @current-items="updateTotal"
          :footer-props="{ 'items-per-page-options': [10, 15, 25, 50, 100] }"
          class="purchase-table"
        >
          <template v-slot:item="{ item }">
            <tr class="pointer purchase-row">
              <td class="text-left" @click="viewPurchase(item)">
                {{ item.date | moment("MM/DD/YYYY") }}
              </td>

              <td class="text-left" @click="viewPurchase(item)">
                <span class="font-weight-bold pr-1">{{ item.localId }}</span>

                <v-chip
                  v-if="Number(item.due || 0) === 0"
                  color="green lighten-5"
                  text-color="green darken-3"
                  small
                  class="font-weight-bold"
                >
                  Paid
                </v-chip>

                <v-chip
                  v-else
                  color="orange lighten-5"
                  text-color="orange darken-3"
                  small
                  class="font-weight-bold"
                >
                  Open
                </v-chip>
              </td>

              <td
                v-if="!VendorId"
                class="text-left"
                @click="viewPurchase(item)"
              >
                {{ item.Vendor ? item.Vendor.name : "Unknown Vendor" }}
              </td>

              <td class="text-left" @click="viewPurchase(item)">
                {{ item.referenceNumber || "--" }}
              </td>

              <td class="text-right font-weight-bold" @click="viewPurchase(item)">
                {{ item.total | currency }}
              </td>

              <td class="text-right font-weight-bold" @click="viewPurchase(item)">
                {{ item.due | currency }}
              </td>

              <td class="justify-end align-center text-right">
                <v-menu bottom left offset-y>
                  <template v-slot:activator="{ on }">
                    <v-btn v-on="on" icon small>
                      <v-icon>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>

                  <v-list dense class="menu-button">
                    <v-list-item @click="viewPurchase(item)">
                      <v-list-item-title>View Purchase</v-list-item-title>
                    </v-list-item>

                    <v-list-item @click="viewPackingSlip(item)">
                      <v-list-item-title>View Packing Slip</v-list-item-title>
                    </v-list-item>

                    <v-list-item
                      v-if="Number(item.due || 0) !== 0"
                      @click="makePayment(item)"
                    >
                      <v-list-item-title>Make Payment</v-list-item-title>
                    </v-list-item>

                    <v-divider v-if="!VendorId" />

                    <v-list-item v-if="!VendorId" @click="viewProfile(item)">
                      <v-list-item-title>Profile</v-list-item-title>
                    </v-list-item>

                    <v-divider />

                    <v-list-item @click="editPurchase(item)">
                      <v-list-item-title>Edit</v-list-item-title>
                    </v-list-item>

                    <v-list-item @click="deletePurchase(item)">
                      <v-list-item-title class="red--text">
                        Delete
                      </v-list-item-title>
                    </v-list-item>

                    <v-list-item @click="createInvoice(item)">
                      <v-list-item-title>Create Invoice</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </td>
            </tr>
          </template>

        
          

          <v-alert slot="no-results" :value="true" color="error" icon="warning">
            Your search for "{{ search }}" found no results.
          </v-alert>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import purchaseService from "../service";
import Hashids from "hashids";
import _ from "lodash";

const DateTimePicker = () => import("@/components/DateTimePicker");
const MakePayment = () =>
  import("@/modules/Inventory/Payment/components/Create.vue");

const hashids = new Hashids();

export default {
  name: "purchase-list",

  props: {
    VendorId: {
      default: null,
    },
    showAll: {
      default: false,
    },
    due: {
      default: false,
    },
  },

  components: {
    DateTimePicker,
    MakePayment,
  },

  data() {
    return {
      totalItems: 0,
      loadingDialog: false,
      mobileFilterSheet: false,
      purchaseStatus: "all",
      fullTotal: 0,
      fullBalance: 0,
      total: 0,
      balance: 0,
      dateTime: {
        start: this.$moment()
          .startOf("day")
          .subtract(60, "days")
          .toISOString(),
        end: this.$moment().endOf("day").toISOString(),
      },
      headers: [],
      Purchases: [],
      search: null,
      loading: true,
      page: 1,
      sortBy: "date",
      pageCount: 0,
      sortDesc: true,
      itemsPerPage: 10,
      makePaymentDialog: false,
      selectedPurchase: null,
    };
  },

  computed: {
    ...mapGetters("global", [
      "currentUser",
      "checkRightStatus",
      "currentBusiness",
    ]),
    ...mapGetters("purchase", ["PurchaseDetails", "getEditPurchaseId"]),
  },

  watch: {
    VendorId: {
      immediate: true,
      handler() {
        this.getList();
      },
    },

    page() {
      this.getList();
    },

    itemsPerPage() {
      this.page = 1;
      this.getList();
    },

    sortBy() {
      this.page = 1;
      this.getList();
    },

    sortDesc() {
      this.page = 1;
      this.getList();
    },

    search: _.debounce(function () {
      this.page = 1;
      this.getList();
    }, 300),
  },

  created() {
    this.loadHeader();
    this.$events.listen("purchaseListEvent", () => this.getList());
    document.title = "smplsale - Purchase List";
  },

  beforeDestroy() {
    this.$events.remove("purchaseListEvent");
  },

  methods: {
    ...mapActions("purchase", ["ResetPurchase"]),

    fixAvgCost(purchase) {
      return purchaseService
        .fixAvgCost(purchase.id)
        .catch((err) => {
          console.log(err);
        });
    },

    updateTotal(val) {
      this.total = val.reduce((sum, row) => {
        return sum + Number(row.total || 0);
      }, 0);

      this.balance = val.reduce((sum, row) => {
        return sum + Number(row.due || 0);
      }, 0);
    },

    makePayment(row) {
      this.selectedPurchase = row;
      this.makePaymentDialog = true;
    },

    openRunning() {
      if (this.getEditPurchaseId) {
        this.$router.push({
          path: `/inventory/purchase/${this.getEditPurchaseId}/edit`,
        });
        return;
      }

      this.$router.push({ path: "/inventory/purchase/create" });
    },

    add() {
      if (this.PurchaseDetails.length !== 0) {
        this.$swal({
          title: "Are you sure?",
          text: "There is a running purchase. Do you want to clear it?",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          cancelButtonText: "No",
          confirmButtonText: "Yes",
        }).then((result) => {
          if (result.value) {
            this.ResetPurchase();
            this.goToCreatePurchase();
          }
        });

        return;
      }

      this.goToCreatePurchase();
    },

    goToCreatePurchase() {
      if (this.VendorId) {
        this.$router.push({
          path: `/inventory/purchase/create?VendorId=${this.VendorId}`,
        });
        return;
      }

      this.$router.push({ path: "/inventory/purchase/create" });
    },

    viewProfile(val) {
      if (!val.VendorId) {
        return;
      }

      this.$router.push({ path: `/inventory/vendor/${val.VendorId}/profile` });
    },

    viewPurchase(purchase) {
      const hashId = hashids.encode(purchase.id);

      const routeData = this.$router.resolve({
        path: `/global/purchase/${hashId}/view`,
      });

      window.open(routeData.href, "newwindow", "width=850, height=1100");
    },

    viewPackingSlip(purchase) {
      const hashId = hashids.encode(purchase.id);

      const routeData = this.$router.resolve({
        path: `/global/purchase/${hashId}/packingSlip`,
      });

      window.open(routeData.href, "newwindow", "width=850, height=1100");
    },

    loadHeader() {
      this.headers = [
        {
          text: this.$t("labels.date"),
          align: "left",
          value: "date",
          sortable: true,
        },
        {
          text: "Purchase Number",
          align: "left",
          value: "localId",
          sortable: true,
        },
      ];

      if (!this.VendorId) {
        this.headers.push({
          text: "Vendor Name",
          value: "Vendor.name",
          align: "left",
          sortable: true,
        });
      }

      this.headers.push(
        {
          text: "Reference Number",
          align: "left",
          value: "referenceNumber",
          sortable: true,
        },
        {
          text: "Total",
          value: "total",
          align: "right",
          sortable: true,
        },
        {
          text: "Due",
          value: "due",
          align: "right",
          sortable: true,
        },
        {
          text: "",
          value: "action",
          sortable: false,
        }
      );
    },

    getList() {
      this.loading = true;

      const where = {};

      if (!this.showAll) {
        where.start = this.dateTime.start;
        where.end = this.dateTime.end;
      }

      if (this.purchaseStatus === "open") {
        where.due = true;
      }

      if (this.purchaseStatus === "paid") {
        where.paid = true;
      }

      if (this.VendorId) {
        where.VendorId = this.VendorId;
      }

      if (this.due) {
        where.due = true;
      }

      const sortBy = Array.isArray(this.sortBy)
        ? this.sortBy[0] || "date"
        : this.sortBy || "date";

      const sortDesc = Array.isArray(this.sortDesc)
        ? !!this.sortDesc[0]
        : !!this.sortDesc;

      const query = {
        ...where,
        search: this.search || "",
        limit: this.itemsPerPage,
        offset: (this.page - 1) * this.itemsPerPage,
        sortBy,
        sortDesc,
        orderBy: [sortBy, String(sortDesc)],
      };

      return purchaseService
        .getAll(query)
        .then((response) => {
          const res = response.data && response.data.rows ? response.data : response;
          const rows = res.rows || res.data || [];
          const cnt = Number(res.count || 0);

          this.totalItems = cnt;
          this.Purchases = rows;

          this.fullTotal = Number(
            res.totals && res.totals.total ? res.totals.total : 0
          );

          this.fullBalance = Number(
            res.totals && res.totals.balance ? res.totals.balance : 0
          );

          this.total = rows.reduce((sum, row) => {
            return sum + Number(row.total || 0);
          }, 0);

          this.balance = rows.reduce((sum, row) => {
            return sum + Number(row.due || 0);
          }, 0);

          return response;
        })
        .catch((err) => {
          console.error("purchase getList failed", err);
          this.Purchases = [];
          this.totalItems = 0;
          this.total = 0;
          this.balance = 0;
          this.fullTotal = 0;
          this.fullBalance = 0;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    editPurchase(purchase) {
      this.$router.push({ path: `/inventory/purchase/${purchase.id}/edit` });
    },

    deletePurchase(purchase) {
      this.$swal({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        cancelButtonText: "No",
        confirmButtonText: "Yes",
      }).then((result) => {
        if (result.value) {
          this.loadingDialog = true;

          purchaseService
            .delete(purchase.id)
            .then((response) => {
              if (response.data) {
                this.$swal("Deleted!", "Purchase has been deleted.", "success");
                this.getList();
                this.$events.emit("VendorProfileEvent");
              }
            })
            .catch((error) => {
              const message =
                error && error.data && error.data.message
                  ? error.data.message
                  : "Unable to delete purchase";

              this.$swal({
                title: "Delete Purchase",
                text: message,
                icon: "error",
              });
            })
            .finally(() => {
              this.loadingDialog = false;
            });
        }
      });
    },

    createInvoice(purchase) {
      this.$swal({
        title: "Are you sure?",
        text: "You want to create invoice with this purchase?",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        cancelButtonText: "No",
        confirmButtonText: "Yes",
      }).then((result) => {
        if (result.value) {
          purchaseService
            .getById(purchase.id)
            .then((obj) => {
              const passData = obj.data.PurchaseDetails;

              this.$router.push({
                name: "invoice-create",
                params: {
                  passData,
                  type: "purchase",
                },
              });
            })
            .catch((err) => {
              console.log("createInvoice error", err);
            });
        }
      });
    },
  },
};
</script>

<style scoped>
.purchase-list-page {
  background: #f8fafc;
}

.purchase-list-card {
  border-radius: 20px;
  background: #ffffff;
}

.purchase-list-header {
  padding: 16px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.purchase-title-wrap {
  display: flex;
  align-items: center;
}

.page-title {
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
}

.page-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}

.purchase-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.purchase-filter-bar {
  padding: 12px 16px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.purchase-search {
  max-width: 340px;
  min-width: 260px;
}

.status-toggle {
  background: #f8fafc;
  border-radius: 14px;
  padding: 2px;
}

.summary-row {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

.summary-box {
  background: #f8fafc;
  border-radius: 16px;
  padding: 14px;
}

.summary-label {
  font-size: 12px;
  color: #64748b;
}

.summary-value {
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
  margin-top: 4px;
}

.purchase-table {
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  overflow: hidden;
}

.purchase-row:hover {
  background: #f8fafc;
}

.table-total-row {
  background: #f8fafc;
}

.table-result-row {
  background: #eef2ff;
}

.mobile-filter-card {
  border-radius: 20px 20px 0 0;
}

.mobile-filter-header {
  padding: 10px 12px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.mobile-filter-title {
  font-size: 16px;
  font-weight: 800;
  color: #0f172a;
}

.mobile-status-toggle {
  width: 100%;
}

.mobile-status-toggle .v-btn {
  flex: 1;
}

@media (max-width: 960px) {
  .summary-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 600px) {
  .purchase-search {
    max-width: none;
    min-width: 0;
    flex: 1;
  }

  .summary-row {
    grid-template-columns: 1fr;
  }

  .purchase-actions {
    width: 100%;
  }

  .purchase-actions .v-btn {
    flex: 1;
  }
}
</style>